import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiError, PageRequest, PageResponse, SortDirection } from "@/types/api";

interface Options<F> {
  initialFilter?: F;
  initialPage?: PageRequest;
}

interface State<T, F> {
  data: PageResponse<T> | null;
  loading: boolean;
  error: ApiError | null;
  filter: F;
  pageRequest: PageRequest;
}

/**
 * Generic hook for paginated, filterable resources backed by the Atlas UI
 * /api/ui/v1 endpoints. Each module wraps this with a thin context-providing
 * hook so callers don't have to pass the fetcher around.
 */
export function usePagedResource<T, F extends object>(
  fetcher: (filter: F, page: PageRequest) => Promise<PageResponse<T>>,
  opts: Options<F> = {},
) {
  const [state, setState] = useState<State<T, F>>({
    data: null,
    loading: false,
    error: null,
    filter: (opts.initialFilter ?? ({} as F)) as F,
    pageRequest: opts.initialPage ?? { page: 0, size: 20, sort: "atlasModifiedAt", direction: "desc" },
  });

  const seq = useRef(0);
  // Several updates can be issued before React renders again (for example, a
  // saved filter followed by its scope refresh). Keep an eager snapshot so
  // each operation builds on the latest requested filter and page.
  const stateRef = useRef(state);

  const refresh = useCallback(
    async (filter: F, pageRequest: PageRequest) => {
      const ticket = ++seq.current;
      stateRef.current = {
        ...stateRef.current,
        loading: true,
        error: null,
        filter,
        pageRequest,
      };
      setState((s) => ({ ...s, loading: true, error: null, filter, pageRequest }));
      try {
        const data = await fetcher(filter, pageRequest);
        if (ticket !== seq.current) return; // stale response — ignore
        setState((s) => {
          return { ...s, data, loading: false };
        });
      } catch (err) {
        if (ticket !== seq.current) return;
        setState((s) => {
          return { ...s, loading: false, error: err as ApiError };
        });
      }
    },
    [fetcher],
  );

  useEffect(() => {
    const current = stateRef.current;
    void refresh(current.filter, current.pageRequest);
  }, [refresh]);

  const setFilter = useCallback(
    (next: Partial<F>) => {
      const current = stateRef.current;
      const merged = { ...current.filter, ...next } as F;
      (Object.keys(next) as (keyof F)[]).forEach((key) => {
        if (next[key] === undefined) {
          delete (merged as Record<string, unknown>)[key as string];
        }
      });
      const reset = { ...current.pageRequest, page: 0 };
      void refresh(merged, reset);
    },
    [refresh],
  );

  /**
   * Drops every filter back to what the resource started with. `pageRequest` is
   * carried over untouched bar the page reset, so the user's chosen sort column
   * and direction survive — clearing filters changes which rows are listed, not
   * how they are ordered.
   */
  const initialFilter = useRef(opts.initialFilter);
  const clearFilter = useCallback(() => {
    void refresh(
      (initialFilter.current ?? ({} as F)) as F,
      { ...stateRef.current.pageRequest, page: 0 },
    );
  }, [refresh]);

  const setPage = useCallback(
    (page: number) => {
      const current = stateRef.current;
      void refresh(current.filter, { ...current.pageRequest, page });
    },
    [refresh],
  );

  const setSort = useCallback(
    (sort: string, direction: SortDirection) => {
      const current = stateRef.current;
      void refresh(current.filter, { ...current.pageRequest, sort, direction, page: 0 });
    },
    [refresh],
  );

  const reload = useCallback(() => {
    const current = stateRef.current;
    void refresh(current.filter, current.pageRequest);
  }, [refresh]);

  /**
   * Reload from the first page. For actions that bump a record to the top of the
   * default `atlasModifiedAt desc` ordering — reloading the current page would
   * leave the caller looking at a window the record has just moved out of.
   */
  const reloadFirstPage = useCallback(() => {
    const current = stateRef.current;
    void refresh(current.filter, { ...current.pageRequest, page: 0 });
  }, [refresh]);

  const upsertEntry = useCallback((entry: T) => {
    setState((s) => {
      if (!s.data) return s;
      const id = (entry as { id?: string }).id;
      if (!id) return s;
      const idx = s.data.content.findIndex((row) => (row as { id?: string }).id === id);
      const isNew = idx < 0;
      const merged = isNew
        ? [entry, ...s.data.content]
        : s.data.content.map((row, i) => (i === idx ? { ...row, ...entry } : row));
      const seen = new Set<string>();
      const content = merged.filter((row) => {
        const rowId = (row as { id?: string }).id;
        if (!rowId || seen.has(rowId)) return false;
        seen.add(rowId);
        return true;
      });
      return {
        ...s,
        data: {
          ...s.data,
          content,
          totalElements: isNew ? s.data.totalElements + 1 : s.data.totalElements,
        },
      };
    });
  }, []);

  return { ...state, setFilter, clearFilter, setPage, setSort, reload, reloadFirstPage, upsertEntry };
}
